import { genBlock } from "@/utils/bem";
import {
  Flex,
  CloseButton,
  Divider,
  Grid,
  Text,
  Spacer,
  chakra,
  ChakraProps,
  LayoutProps,
  Center,
  GridItem,
} from "@chakra-ui/react";
import { observer } from "mobx-react-lite";
import React, { useContext, useMemo, useState } from "react";
import classNames from "classnames";
import { langStore, translate } from "@noli/business";

import { Search } from "@/components/Search";
import { RadioOptionType, RadioTagGroup } from "@/components/Radio/RadioTag";
import { themeStore } from "@/stores/theme";
import { Editor_Words } from "@/utils/lang/editor";
import getScrollBarProps from "@/styles/scrollBarProps";

import { KeywordItemCard } from "../KeywordItemCard";
import { WordTableContext } from "../context";
import { categoryMap } from "../utils";
import { Categories, WordEntityTypeWithParentKeys } from "../type";
import "./style.scss";

const block = genBlock("c-keyword-list");
const TitleText = chakra(Text, {
  baseStyle: {
    fontWeight: 500,
    fontSize: { base: "14px", lg: "16px" },
    whiteSpace: "nowrap",
  },
});
const MARGIN_X = 35;
const dividerStyle = (margin: number): ChakraProps => ({
  bgColor: "text.primary",
  // mt: "60px",
  maxW: "100%",
  mx: `${margin}px`,
  w: `calc(100% - ${margin * 2}px)`,
});
const defaultContainerWidth: LayoutProps["w"] = {
  base: "fit-content",
  lg: "1034px",
  md: "900px",
};
const defaultContainerMaxWidth: LayoutProps["maxW"] = {
  base: "900px",
  lg: "1034px",
  md: "900px",
};
const defaultContainerMaxHeight: LayoutProps["maxH"] = {
  base: "450px",
  lg: "534px",
  md: "450px",
};

const IKeywordList: React.FC<{
  list: WordEntityTypeWithParentKeys[];
  onClose?: () => void;
}> = ({ list, onClose }) => {
  const {
    tablePopupProps,
    nameDict,
    tableTitle,
    searchPlaceholder,
    category,
    onCategoryChange,
    tableRightSide,
    TableItemRender = KeywordItemCard,
    keywordListConfig,
    searchFilter,
  } = useContext(WordTableContext);
  const { selectColor } = themeStore.styles;
  const lang = langStore.tgt;
  const {
    containerWidth = defaultContainerWidth,
    containerMaxHeight = defaultContainerMaxHeight,
    containerMaxWidth = defaultContainerMaxWidth,
    columnCount,
  } = keywordListConfig ?? {};
  const [searchResult, setSearchResult] = useState<WordEntityTypeWithParentKeys[] | null>(null);
  const handleSearch = (value?: string) => {
    if (!value) {
      setSearchResult(null);
      return;
    }
    const matchItems = searchFilter?.(list, nameDict)(value) ?? null;
    setSearchResult(matchItems);
  };
  const cateOptions = useMemo(() => {
    const categories = list?.[0]?.categories ?? [];
    if (!categories?.length) return [];
    return categories.map((cate) => {
      const option: RadioOptionType<Categories> = {
        label: categoryMap[cate].label[lang],
        value: cate,
        css: { borderColor: "transparent", whiteSpace: "nowrap" },
      };
      return option;
    });
  }, []);

  const showList = useMemo(() => {
    if (searchResult !== null) {
      return searchResult;
    }
    return list ?? [];
  }, [list, searchResult]);
  const { className } = tablePopupProps ?? {};
  return (
    <Flex
      flexDir="column"
      className={classNames(block(), className)}
      w={containerWidth}
      maxW={containerMaxWidth}
      minW="fit-content"
      maxH={containerMaxHeight}
      rounded="12px"
      bg="#3A3A3A"
      boxShadow="0px 0px 24px rgba(0, 0, 0, 0.25)"
      pos="relative"
      {...tablePopupProps}
    >
      <CloseButton
        onClick={onClose}
        color="white"
        pos="absolute"
        right="10px"
        top="10px"
        fontSize="16px"
        zIndex={5}
      />

      <Flex
        className={block({ e: "header" })}
        h="60px"
        p={`20px ${MARGIN_X + 10}px 12px ${MARGIN_X}px`}
        color="text.secondary"
        w="100%"
        minW="fit-content"
        gap="4px"
        alignItems="center"
      >
        <TitleText>{translate(tableTitle ?? Editor_Words["keyword-list-title"], lang)}</TitleText>
        <Search
          maxW="260px"
          minW="150px"
          flex={1}
          ml={{ base: "8px", lg: "20px" }}
          onSearch={handleSearch}
          className={block({ e: "search" })}
          placeholder={searchPlaceholder ? translate(searchPlaceholder, lang) : undefined}
        />

        <Spacer />
        {tableRightSide ||
          (cateOptions?.length ? (
            <>
              <TitleText>{translate(Editor_Words["image-content-categories"], lang)}：</TitleText>
              <RadioTagGroup
                className={block({ e: "categories" })}
                gap="4px"
                options={cateOptions}
                // mr={{ base: "10px", lg: "12px" }}
                useRadioGroupProps={{
                  value: category,
                  onChange: (value) => onCategoryChange?.(value as Categories),
                }}
              />
            </>
          ) : null)}
      </Flex>

      <Divider {...dividerStyle(MARGIN_X)} />

      <Grid
        className={block({ e: "options" })}
        templateColumns={{
          base: `repeat(${columnCount ?? 6}, 1fr)`,
          md: `repeat(${columnCount ?? 8}, 1fr)`,
          lg: columnCount ? `repeat(${columnCount}, 1fr)` : "repeat(auto-fill, 108px)",
        }}
        gridAutoRows="max-content"
        gridAutoFlow="row"
        pr={`${MARGIN_X - 8}px`}
        pl={`${MARGIN_X}px`}
        pb="13px"
        mt="13px"
        gap="14px"
        minH="200px"
        // w="100%"
        pos="relative"
        {...getScrollBarProps({ autoMargin: false })}
      >
        {searchResult && !searchResult.length ? (
          <Center
            w="100%"
            pos="absolute"
            color={selectColor}
            _hover={{ color: selectColor }}
            h="100%"
            pb="24px"
          >
            {translate(Editor_Words["search-not-found"], lang)}
          </Center>
        ) : (
          showList.map((child) => <TableItemRender data={child} key={child.key} />)
        )}
      </Grid>
    </Flex>
  );
};
export const KeywordList = observer(IKeywordList);
